import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type { ChatFlow, ChatFlowContext, ChatFlowEventResult } from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

/**
 * Incorporation 전용 챗봇 플로우
 * T-Link entity 선택 → 우측 Q&A 패널에서 진행
 */
export const incorporationFlow: ChatFlow = {
  id: 'incorporation',
  categoryMatch: ['incorporation'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content: `You selected **${preSet.task.TASK_NAME}** (Incorporation).\n\n` +
          `This preset includes ${docCount} document(s).\n\n` +
          `Would you like to load data from **T-Link** to auto-fill the documents?`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'select-entity',
      aiMessage: 'Select an entity from T-Link Import, or skip to proceed manually.',
      widget: 'setup-entity',
      widgetConfig: {
        targetVarKey: '_tlinkEntity',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'qa-ready',
      aiMessage: 'Entity linked. Please proceed with the Q&A on the right panel to set up your incorporation documents.',
      widget: null,
      nextStep: null,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    // T-Link entity 선택 (auto-fill 버튼 클릭 시)
    if (eventName === 'setup-entity-autofill' && context.currentStepId === 'select-entity') {
      const icpCd = payload?._icpCd || payload?.entityCd || '';
      const entityName = payload?._entityName || payload?.companyName || 'Entity';
      return {
        messages: [
          {
            role: 'user',
            content: `Linked entity: **${entityName}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd: icpCd,
          entityName,
          icpCd,
        },
        nextStepId: 'qa-ready',
      };
    }

    // entity-field-select: entity 드롭다운 선택 시 (detail 로딩 완료 후 → 우측 바인딩 + qa-ready)
    if (eventName === 'entity-field-select' && context.currentStepId === 'select-entity') {
      try {
        const parsed = JSON.parse(payload);
        if (parsed?._icpCd) {
          return {
            messages: [
              {
                role: 'user',
                content: `Selected: **${parsed._entityName || 'Entity'}**`,
                timestamp: Date.now(),
                stepId: 1,
              },
            ],
            formUpdates: {
              entityCd: parsed._icpCd,
              entityName: parsed._entityName || '',
              icpCd: parsed._icpCd,
            },
            nextStepId: 'qa-ready',
          };
        }
      } catch { /* not JSON, ignore */ }
      return {};
    }

    // Entity 선택 스킵
    if (eventName === 'value-submit' && context.currentStepId === 'select-entity') {
      return {
        messages: [
          {
            role: 'user',
            content: 'Skipped entity linking. Proceeding with manual Q&A.',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'qa-ready',
      };
    }

    return {};
  },
};
